import { useTranslation } from "react-i18next";

export interface ReportZoneDistributionRow {
  zone: string;
  count: number;
}

export interface ReportZoneDistributionProps {
  rows: ReportZoneDistributionRow[];
  /** Same rule as `ReportSectionTable`'s `showTitle` — suppress the repeated
   * panel title when this is the section's only content and the enclosing
   * `<section>`'s own `<h2>` already states the same title. */
  showTitle?: boolean;
}

const ZONE_ORDER = ["above", "neutral", "below"] as const;

/** Adapts Analytics' own premium zone-distribution language (compact
 * segmented bar + legend, never a donut/pie — task spec §10, matching
 * `ZoneDistribution.tsx`'s own documented rationale) to the report
 * builder's generic `{zone, count}` table shape (`report-builder.service.ts`'s
 * `zoneTable`) — presentational only, no calculation. Renders in place of the
 * generic `ReportSectionTable` specifically for the `zoneDistribution`
 * section (`ReportDetailPage.tsx`), never as a new backend contract. */
export function ReportZoneDistribution({ rows, showTitle = true }: ReportZoneDistributionProps) {
  const { t } = useTranslation("reports");

  const countByZone = Object.fromEntries(rows.map((r) => [r.zone, r.count]));
  const total = ZONE_ORDER.reduce((sum, zone) => sum + (countByZone[zone] ?? 0), 0);
  const pct = (count: number) => (total > 0 ? (count / total) * 100 : 0);

  if (total === 0) return null;

  return (
    <div className="nx-report-panel">
      {showTitle ? <p className="nx-report-panel__title">{t("sections.zoneDistribution")}</p> : null}
      <div
        className="nx-report-zone-distribution__bar"
        role="img"
        aria-label={ZONE_ORDER.map((zone) => `${t(`zone.${zone}`)}: ${countByZone[zone] ?? 0}`).join(", ")}
      >
        {ZONE_ORDER.map((zone) =>
          (countByZone[zone] ?? 0) > 0 ? (
            <div key={zone} className={`nx-report-zone-distribution__segment nx-report-zone-distribution__segment--${zone}`} style={{ width: `${pct(countByZone[zone] ?? 0)}%` }} />
          ) : null,
        )}
      </div>
      <div className="nx-report-zone-distribution__legend">
        {ZONE_ORDER.map((zone) => (
          <span key={zone} className="nx-report-zone-distribution__legend-item">
            <span className={`nx-report-zone-distribution__swatch nx-report-zone-distribution__swatch--${zone}`} aria-hidden="true" />
            {t(`zone.${zone}`)} <span className="nx-report-zone-distribution__legend-count">{countByZone[zone] ?? 0}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
